import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, } from 'react';
import { cn } from '@/core/utils/cn';
const ToastContext = createContext(null);
const toastVariants = cva('pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-surface-2 p-4 shadow-modal dark:shadow-modal-dark data-[state=open]:animate-slide-up', {
    variants: {
        tono: {
            success: 'border-success',
            warning: 'border-warning',
            danger: 'border-danger',
            info: 'border-info',
        },
    },
    defaultVariants: { tono: 'info' },
});
const ICONOS = {
    success: CheckCircle2,
    warning: TriangleAlert,
    danger: AlertCircle,
    info: Info,
};
const COLOR_ICONO = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info',
};
export function ToastProvider({ children }) {
    const [items, setItems] = useState([]);
    const mostrar = useCallback((t) => {
        const id = crypto.randomUUID();
        setItems((prev) => [...prev, { id, ...t }]);
    }, []);
    const quitar = useCallback((id) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
    }, []);
    const valor = useMemo(() => ({
        mostrar,
        exito: (titulo, descripcion) => mostrar({ titulo, descripcion, tono: 'success' }),
        error: (titulo, descripcion) => mostrar({ titulo, descripcion, tono: 'danger' }),
        advertir: (titulo, descripcion) => mostrar({ titulo, descripcion, tono: 'warning' }),
        info: (titulo, descripcion) => mostrar({ titulo, descripcion, tono: 'info' }),
    }), [mostrar]);
    return (_jsx(ToastContext.Provider, { value: valor, children: _jsxs(ToastPrimitive.Provider, { swipeDirection: "right", children: [children, items.map((item) => {
                    const Icono = ICONOS[item.tono];
                    return (_jsxs(ToastPrimitive.Root, { duration: item.tono === 'danger' ? 8000 : 4000, onOpenChange: (open) => {
                            if (!open)
                                quitar(item.id);
                        }, className: toastVariants({ tono: item.tono }), children: [_jsx(Icono, { className: cn('mt-0.5 h-5 w-5 flex-shrink-0', COLOR_ICONO[item.tono]) }), _jsxs("div", { className: "flex-1", children: [_jsx(ToastPrimitive.Title, { className: "text-sm font-semibold text-text-primary", children: item.titulo }), item.descripcion ? (_jsx(ToastPrimitive.Description, { className: "mt-0.5 text-sm text-text-secondary", children: item.descripcion })) : null] }), _jsx(ToastPrimitive.Close, { "aria-label": "Cerrar", className: "rounded p-0.5 text-text-tertiary hover:text-text-primary", children: _jsx(X, { className: "h-4 w-4" }) })] }, item.id));
                }), _jsx(ToastPrimitive.Viewport, { className: "fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col gap-2 outline-none" })] }) }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast requiere <ToastProvider />');
    return ctx;
}
